import React, { useEffect, useState, useMemo, useCallback } from 'react';
import MainText from '../../components/MainText';
import { AccountBalance, Business, Check, ContentPaste, Delete, DeleteRounded, Download, Edit, EditNote, Refresh, RestoreFromTrash, Search, Storefront, UploadFile, Visibility } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField, Chip, LinearProgress, Typography, IconButton, Tooltip, Checkbox, Avatar } from '@mui/material';
import DatePick from '../../components/DatePick';
import { useDispatch, useSelector } from 'react-redux';
import { addTab, clearFlow, mergeFlow } from '../../tabs/tabsSlice';
import API, { RBAC } from '../../services/api';
import { DataGrid } from '@mui/x-data-grid';
import IconOrderPDF from '../../components/pdf/IconOrderPDF';
import EditOrder from './EditOrder';
import CleanningButton from '../../components/CleanningButton';
import CustomSnackBar from '../../components/CustomSnackBar';
import CustomSwitch from '../../components/CustomSwitch';
import CustomModal from '../../components/CustomModal';
import ViewInvoiceDetail from '../Invoice/ViewInvoiceDetail';
import { useAuth } from '../../auth/AuthProvider';
const initials = (fn = '', ln = '', un = '') => {
    const a = (fn || '').trim()[0] || '';
    const b = (ln || '').trim()[0] || '';
    const base = (a + b) || (un || '').trim()[0] || '?';
    return base.toUpperCase();
};
const UserCell = ({ u }) => {
    const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || u?.username || '—';
    return (
        <Box 
            sx={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'flex-start',
                gap: 1 
            }}
        >
            <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
                {initials(u?.first_name, u?.last_name, u?.username)}
            </Avatar>
            <Typography variant="body2">{name}</Typography>
        </Box>
    );
};
const formatYMD = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";
  // 1) Coincidencia exacta YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  // 2) ISO o cualquier cosa que empiece con YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, Y, M, D] = m;
    return `${D}/${M}/${Y}`;
  }
  // 3) Último intento: Date nativa
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  // 4) No se pudo formatear
  return "Sin fecha";
};
const normalizeToDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'object') {
    if (v.$d instanceof Date) return v.$d; // dayjs
    if (typeof v.toDate === 'function') return v.toDate(); // moment/dayjs
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toLocalYMD = (v) => {
  const dt = normalizeToDate(v);
  if (!dt) return undefined;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const formatDateLabel = (v) => {
  const dt = normalizeToDate(v);
  if (!dt) return '';
  return dt.toLocaleDateString();
};

const BANK_REQUISITION_FLOW = 'BANK_REQUISITION';

const formatGTQ = (value) => new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
}).format(Number(value || 0));

function OrderAbastoDialog({ open, order, onClose, onSaved, onNotify }) {
    const supplier = (
        order?.supplier_data && typeof order.supplier_data === 'object'
            ? order.supplier_data
            : (order?.supplier && typeof order.supplier === 'object' ? order.supplier : {})
    );
    const supplierId = order?.supplier_id || supplier?.id || null;
    const [requisition, setRequisition] = useState(order?.bank_requisition || null);
    const [form, setForm] = useState({
        requisition_number: '',
        requisition_date: '',
        requested_amount: '',
    });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fillForm = useCallback((req) => {
        setForm({
            requisition_number: req?.requisition_number || '',
            requisition_date: req?.requisition_date || '',
            requested_amount: req?.requested_amount || '',
        });
    }, []);

    useEffect(() => {
        if (!open || !order?.id) return undefined;
        let mounted = true;
        setFile(null);
        setLoading(true);
        API.get('bank-requisition/', { params: { purchase_order: order.id } })
            .then(({ data }) => {
                if (!mounted) return;
                const items = Array.isArray(data) ? data : data?.results || [];
                const req = items[0] || order?.bank_requisition || null;
                setRequisition(req);
                fillForm(req);
            })
            .catch((error) => {
                console.error('Error cargando Abasto', error);
                if (mounted) {
                    setRequisition(order?.bank_requisition || null);
                    fillForm(order?.bank_requisition || null);
                }
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [fillForm, open, order?.bank_requisition, order?.id]);

    const handleChange = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleSave = async () => {
        if (!order?.id || !supplierId) return;
        try {
            setSaving(true);
            const fd = new FormData();
            fd.append('purchase_order_id', order.id);
            fd.append('provider_id', supplierId);
            fd.append('bank_name', supplier?.abasto_bank_name || requisition?.bank_name || '');
            fd.append('requisition_number', form.requisition_number || '');
            if (form.requisition_date) fd.append('requisition_date', form.requisition_date);
            if (form.requested_amount !== '') fd.append('requested_amount', form.requested_amount);
            fd.append('status', file ? 'ABASTO_LOADED' : (requisition?.status || 'PENDING_ABASTO'));
            if (file) fd.append('document', file);

            const config = { headers: { 'Content-Type': 'multipart/form-data' } };
            const response = requisition?.id
                ? await API.patch(`bank-requisition/${requisition.id}/`, fd, config)
                : await API.post('bank-requisition/', fd, config);

            setRequisition(response.data);
            fillForm(response.data);
            setFile(null);
            onNotify?.('Abasto de orden guardado', 'success');
            await onSaved?.();
            onClose?.();
        } catch (error) {
            console.error('Error guardando Abasto', error);
            onNotify?.(error.response?.data?.detail || error.response?.data?.message || 'Error al guardar Abasto', 'error');
        } finally {
            setSaving(false);
        }
    };

    const documentUrl = requisition?.document_url || requisition?.document || '';
    const referenceRows = [
        ['Banco', supplier?.abasto_bank_name || requisition?.bank_name || '-'],
        ['Convenio', supplier?.abasto_agreement_name || '-'],
        ['Numero convenio', supplier?.abasto_agreement_number || '-'],
        ['Limite de credito', supplier?.isCredit ? formatGTQ(supplier?.creditLimit) : 'Contado'],
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <AccountBalance sx={{ color: '#10173c' }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Abasto de orden
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Orden {order?.public_code || order?.id || ''}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.25,
                        mb: 1.5,
                        bgcolor: 'rgba(16, 23, 60, 0.03)',
                    }}
                >
                    <Grid container spacing={1}>
                        {referenceRows.map(([label, value]) => (
                            <Grid item xs={12} sm={6} key={label}>
                                <Typography variant="caption" color="text.secondary">
                                    {label}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {value}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Numero de requisicion"
                            value={form.requisition_number}
                            onChange={handleChange('requisition_number')}
                            disabled={loading || saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Fecha"
                            value={form.requisition_date || ''}
                            onChange={handleChange('requisition_date')}
                            InputLabelProps={{ shrink: true }}
                            disabled={loading || saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Monto"
                            value={form.requested_amount}
                            onChange={handleChange('requested_amount')}
                            inputProps={{ min: 0, step: '0.01' }}
                            disabled={loading || saving}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFile />}
                        disabled={loading || saving}
                        sx={{ textTransform: 'none' }}
                    >
                        {file ? file.name : (documentUrl ? 'Reemplazar documento' : 'Subir imagen/documento')}
                        <input
                            hidden
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(event) => setFile(event.target.files?.[0] || null)}
                        />
                    </Button>
                    {documentUrl && (
                        <>
                            <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => window.open(documentUrl, '_blank', 'noopener,noreferrer')}
                                sx={{ textTransform: 'none' }}
                            >
                                Ver
                            </Button>
                            <Button
                                size="small"
                                startIcon={<Download />}
                                component="a"
                                href={documentUrl}
                                target="_blank"
                                rel="noreferrer"
                                download
                                sx={{ textTransform: 'none' }}
                            >
                                Descargar
                            </Button>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button size="small" variant="contained" onClick={handleSave} disabled={loading || saving}>
                    {saving ? 'Guardando' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ListOrder() {
    const dispatch = useDispatch();
    const savedFilters = useSelector((state) => state.tabs.flows.listOrder || null);
    const { isSuperuser, company } = useAuth();
    const [orders, setOrders] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectionModel, setSelectionModel] = useState([]);
    const [open, setOpen] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openAbasto, setOpenAbasto] = useState(false);
    const [abastoOrder, setAbastoOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const companyId = company?.id || null;
    const [filters, setFilters] = useState({
        search: '',
        startDate: null,
        endDate: null,
        status: 'todas',
        active: true,
    });

    const [debouncedSearch, setDebouncedSearch] = useState('');
    // hydrate filters from saved flow on mount
    useEffect(() => {
        if (savedFilters && typeof savedFilters === 'object') {
            setFilters((prev) => ({ ...prev, ...savedFilters }));
            if (typeof savedFilters.search === 'string') setDebouncedSearch(savedFilters.search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(filters.search), 500);
        return () => clearTimeout(handler);
    }, [filters.search]);

    // persist filters to redux flow for tab restore
    useEffect(() => {
        const handler = setTimeout(() => {
            dispatch(mergeFlow({ name: 'listOrder', data: filters }));
        }, 250);
        return () => clearTimeout(handler);
    }, [dispatch, filters]);
    useEffect(() => {
        return () => {
            dispatch(clearFlow('listOrder'));
        };
    }, [dispatch]);

    const [openSnack, setOpenSnack] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("info");

    useEffect(() => {
        dispatch(addTab({ label: 'Órdenes', path: '/orders/all-orders' }));
    }, [dispatch]);

    useEffect(() => {
        if (!isSuperuser) {
            setCompanyOptions([]);
            return;
        }
        let active = true;
        (async () => {
            try {
                const { data } = await RBAC.companies.list();
                if (!active) return;
                const items = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
                setCompanyOptions(items);
            } catch (error) {
                console.error("Error obteniendo empresas", error);
                if (active) setCompanyOptions([]);
            }
        })();
        return () => { active = false; };
    }, [isSuperuser]);

    const toPercent = (p) => {
        if (p == null) return 0;
        const num = typeof p === 'string' ? parseFloat(p) : p;
        if (Number.isNaN(num)) return 0;
        const clamped = Math.max(0, Math.min(num, 1));
        return Math.round(clamped * 100);
    };
    const hasStartDate = Boolean(normalizeToDate(filters.startDate));
    const hasEndDate = Boolean(normalizeToDate(filters.endDate));
    const showTodayChip = !hasStartDate && !hasEndDate;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {
                search: debouncedSearch || undefined,
                status: filters.status !== 'todas' ? filters.status : undefined,
                active: filters.active,
            };

            const pad = (n) => String(n).padStart(2, '0');
            const today = new Date();
            const todayLocal = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

            let startYMD = toLocalYMD(filters.startDate) || null;
            let endYMD = toLocalYMD(filters.endDate) || null;

            if (!startYMD && !endYMD) {
                startYMD = todayLocal;
                endYMD = todayLocal;
            } else if (startYMD && !endYMD) {
                endYMD = todayLocal;
            } else if (!startYMD && endYMD) {
                startYMD = todayLocal;
            }

            params.start_date = startYMD;
            params.end_date = endYMD;

            const response = await API.get('order/', { params });
            console.log("Datos de órdenes obtenidos:", response.data);
            const formattedOrders = response.data.map(order => ({
                id: order.id,
                ...order,
                supplier: order.supplier?.companyName || order.order_details[0]?.product?.supplier?.companyName || 'Desconocido',
                supplier_data: order.supplier || null,
                supplier_id: order.supplier?.id || order.supplier_id || null,
                supplier_payment_flow_type: order.supplier?.payment_flow_type || 'NORMAL',
                dateTime: formatYMD(order.dateTime),
                createdAt: formatDateTime(order.createdAt),
                bonusDiscount: formatCurrency(order.bonusDiscount),
                priceDiscount: formatCurrency(order.priceDiscount),
                comment: order.comment || '-',
                commentSupplier: order.commentSupplier || '-',
                currency: order.currency || '-',
                orderStatus: order.orderStatus || 'Sin estado',
                storage: order.storage || 'Sin bodega',
                subTotal: formatCurrency(order.subTotal),
                total: formatCurrency(order.total),
                progress: toPercent(order.progress) || 0,
                ...(isSuperuser ? {} : {
                    company: null,
                    company_id: null,
                    company_name: null,
                    companyName: null,
                }),
            }));
            setOrders(formattedOrders);
          
        } catch (error) {
            console.error("Error obteniendo datos", error);
        } finally {
            setIsLoading(false);
        }
    }, [debouncedSearch, filters.startDate, filters.endDate, filters.status, filters.active, isSuperuser, companyId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setSelectionModel([]);
        setSelectedOrder(null);
        setOrders([]);
    }, [companyId]);

    const formatDateTime = useCallback((dateString) => {
        if (!dateString) return 'Sin fecha';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Fecha inválida';
            return date.toLocaleString('es-GT', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (e) {
            console.error("Error formateando fecha:", dateString);
            return 'Fecha inválida';
        }
    }, []);

    const formatCurrency = useCallback((value) => {
        return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(value || 0);
    }, []);

    const getStatusChip = useCallback((status) => {
        const statusMap = {
            'PE': { label: 'Pendiente de Entrega', color: 'primary' },
            'EP': { label: 'Entrega Parcial', color: 'warning' },
            'FI': { label: 'Finalizado', color: 'success' },
            'EX': { label: 'Vencida', color: 'error' }
        };
        return statusMap[status] ? <Chip label={statusMap[status].label} variant="outlined" color={statusMap[status].color} /> : '-';
    }, []);

    const companyNameMap = useMemo(() => {
        const map = new Map();
        (companyOptions || []).forEach((c) => {
            const id = Number(c?.id);
            if (!Number.isNaN(id)) {
                map.set(id, c?.name || c?.companyName || c?.legalName || `Empresa ${id}`);
            }
        });
        return map;
    }, [companyOptions]);


    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectionChange = useCallback((newSelection) => {
        setSelectionModel(newSelection);
        const selectedId = newSelection[0];
        const selected = orders.find(order => order.id === selectedId);
        setSelectedOrder(selected || null);
    }, [orders]);

    const handleEditClick = useCallback((order) => {
        setSelectedOrder(order);
        setOpen(true);
    }, []);

    const handleViewClick = useCallback((order) => {
        setSelectedOrder(order);
        setOpenView(true);
    }, []);

    const handleAbastoClick = useCallback((order) => {
        setAbastoOrder(order);
        setOpenAbasto(true);
    }, []);

    const handleAbastoNotify = useCallback((message, severity = 'info') => {
        setSnackMessage(message);
        setSnackSeverity(severity);
        setOpenSnack(true);
    }, []);


const columns = useMemo(() => {
  const cols = [
  {
    field: 'selection',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Checkbox
          checked={selectionModel.includes(params.row.id)}
          onChange={() => {}}
          inputProps={{ 'aria-label': 'controlled' }}
        />
      </Box>
    ),
  },
  
  {
    field: 'id',
    headerName: 'ID',
    width: 90,
    sortable: true,
    filterable: true,
    headerClassName: 'super-app-theme--header',
  },
{
  field: 'public_code',
  headerName: 'Código',
  width: 140,
  sortable: true,
  filterable: true,
  headerAlign: 'center',
  align: 'center',
  renderHeader: (params) => (
    <Typography 
      variant="subtitle2" 
      sx={{ 
        fontWeight: 600,
        letterSpacing: '0.5px',
        color: 'text.primary',
      }}
    >
      Código
    </Typography>
  ),
  renderCell: (params) => {
    const hasCode = params.value && params.value.trim() !== '';
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {hasCode ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 80,
              px: 1.5,
              py: 0.75,
              backgroundColor: (theme) => 
                theme.palette.mode === 'dark' 
                  ? 'rgba(144, 202, 249, 0.12)' 
                  : 'rgba(33, 150, 243, 0.06)',
              border: '1px solid',
              borderColor: (theme) => 
                theme.palette.mode === 'dark' 
                  ? 'rgba(144, 202, 249, 0.3)' 
                  : 'rgba(33, 150, 243, 0.2)',
              borderRadius: '4px',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: 'success.main',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? 'primary.light' 
                    : 'primary.dark',
                letterSpacing: '0.3px',
                fontFamily: '"Roboto Mono", monospace',
                fontSize: '0.8rem',
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              px: 2,
              py: 0.5,
              borderRadius: '4px',
              backgroundColor: 'action.selected',
              border: '1px solid transparent',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                fontStyle: 'italic',
              }}
            >
              —
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
},
  {
    field: 'supplier',
    headerName: 'Proveedor',
    width: 200,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Fecha Creación',
    width: 150,
    align: 'center',
    headerAlign: 'center',
        renderCell: ( params ) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                {params.value}
              </Typography>
            </Box>
          )
  },
  {
    field: 'orderStatus',
    headerName: 'Estado',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        {getStatusChip(params.value)}
      </Box>
    ),
  },
  {
    field: 'dateTime',
    headerName: 'Fecha Entrega',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2">{params.value}</Typography>
      </Box>
    ),
  },
  {
    field: 'storage',
    headerName: 'Bodega Destino',
    width: 170,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%',
        }}
      >
        <Storefront sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body2">{params.value}</Typography>
      </Box>
    ),
  },
  {
    field: 'comment',
    headerName: 'Notas',
    width: 150,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
        <Tooltip title={params.value || 'Sin notas'}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {params.value || '—'}
          </Typography>
        </Tooltip>
      </Box>
    ),
  },
  {
    field: 'commentSupplier',
    headerName: 'Comentario Proveedor',
    width: 170,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
        <Tooltip title={params.value || 'Sin comentarios'}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {params.value || '—'}
          </Typography>
        </Tooltip>
      </Box>
    ),
  },
  {
    field: 'currency',
    headerName: 'Moneda',
    width: 70,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {params.value}
        </Typography>
      </Box>
    ),
  },

  // 🔢 Columnas numéricas: YA VIENEN FORMATEADAS CON formatCurrency EN fetchData
  {
    field: 'total',
    headerName: 'Total',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {params.value /* ej: "Q 1,234.50" */}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'priceDiscount',
    headerName: 'Desc. Precio',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
          {params.value /* ya viene de formatCurrency(order.priceDiscount) */}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'bonusDiscount',
    headerName: 'Desc. Bonificación',
    width: 180,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
          {params.value /* formatCurrency(order.bonusDiscount) */}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'subTotal',
    headerName: 'Sub-total',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {params.value /* formatCurrency(order.subTotal) */}
        </Typography>
      </Box>
    ),
  },

  // Hecho por
  {
    field: 'hecho_por',
    headerName: 'Hecho por',
    width: 200,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <UserCell u={row?.createdBy} />
      </Box>
    ),
  },
  ...(isSuperuser ? [
    {
      field: "company",
      headerName: "Empresa",
      width: 200,
      renderCell: ({ row }) => {
        const companyId = row.company?.id || row.company_id || (typeof row.company === 'number' ? row.company : null);
        const resolvedName =
          row.company?.name ||
          row.company_name ||
          row.companyName ||
          (Number.isFinite(Number(companyId)) ? companyNameMap.get(Number(companyId)) : null) ||
          (typeof row.company === 'string' ? row.company : null) ||
          "-";
        
        const hasCompany = resolvedName !== '-';
        
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              px: 1.5,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                py: 0.75,
                px: 1,
                borderRadius: '6px',
                background: hasCompany 
                  ? 'linear-gradient(135deg, rgba(6, 95, 70, 0.04) 0%, rgba(16, 185, 129, 0.02) 100%)'
                  : 'transparent',
                border: `1px solid ${hasCompany ? 'rgba(6, 95, 70, 0.1)' : 'transparent'}`,
                overflow: 'hidden',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': hasCompany && {
                  background: 'linear-gradient(135deg, rgba(6, 95, 70, 0.07) 0%, rgba(16, 185, 129, 0.04) 100%)',
                  borderColor: 'rgba(6, 95, 70, 0.2)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(6, 95, 70, 0.1)',
                },
                '&::before': hasCompany && {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: 'linear-gradient(180deg, #065f46 0%, #10b981 100%)',
                }
              }}
            >
              {/* Contenido */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                {hasCompany && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '5px',
                      background: 'linear-gradient(135deg, #10173c 0%, #1e2a5a 100%)',
                      color: 'white',
                      flexShrink: 0,
                      boxShadow: '0 2px 4px rgba(6, 95, 70, 0.2)',
                    }}
                  >
                    <Business sx={{ fontSize: 13 }} />
                  </Box>
                )}
                
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: hasCompany ? 700 : 400,
                      fontSize: '0.85rem',
                      color: hasCompany ? ' #10173c' : '#1e2a5a',
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: hasCompany ? "'Inter', system-ui, sans-serif" : 'inherit',
                      letterSpacing: hasCompany ? '0.01em' : 'normal',
                    }}
                  >
                    {resolvedName}
                  </Typography>
                  
                  {/* ID sutil debajo */}
                  {Number.isFinite(Number(companyId)) && hasCompany && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(6, 95, 70, 0.5)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        display: 'block',
                        letterSpacing: '0.03em',
                      }}
                    >
                      Empresa #{companyId}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      }
    },
  ] : []),

{
    field: 'progress',
    headerName: 'Progreso',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box
        sx={{
          // Contenedor principal para centrar el contenido
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center', // Centrado en Y
          alignItems: 'center',     // Centrado en X
          height: '100%',
          width: '100%',            // Usa todo el ancho de la celda
          py: 0.5,
          px: 1, // Padding horizontal para que la barra no toque los bordes de la celda
        }}
      >
        <Typography 
          align="center" 
          variant="caption" 
          color="textSecondary" 
          sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.7rem' }}
        >
          {params.value} %
        </Typography>
        <LinearProgress
          variant="determinate"
          value={params.value}
          sx={{
            width: '100%',
            height: 8, // Grosor Aumentado (De 6 a 8)
            borderRadius: 4, // Bordes más redondeados
            // Colores del track
            backgroundColor: (theme) =>
              params.value < 50 ? theme.palette.warning.light : theme.palette.success.light,
            // Colores de la barra llena
            '& .MuiLinearProgress-bar': {
              transition: 'transform .3s linear',
              backgroundColor: (theme) =>
                params.value < 50 ? theme.palette.warning.main : theme.palette.success.main,
            },
          }}
        />
      </Box>
    ),
  },
  // Acciones
  {
    field: 'actions',
    headerName: 'Acciones',
    width: 155,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        {params.row.is_active ? (
          <Box>
            <Tooltip title="Editar">
              <IconButton color="primary" onClick={() => handleEditClick(params.row)} size="small">
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ver facturación">
              <IconButton
                color="primary"
                onClick={() => handleViewClick(params.row)}
                size="small"
                sx={{ ml: 0.5 }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {params.row.supplier_payment_flow_type === BANK_REQUISITION_FLOW && (
              <Tooltip title={params.row.bank_requisition?.id ? "Editar Abasto" : "Cargar Abasto"}>
                <IconButton
                  color="info"
                  onClick={() => handleAbastoClick(params.row)}
                  size="small"
                  sx={{ ml: 0.5 }}
                >
                  <AccountBalance fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <>
            <Tooltip title="Restaurar orden">
              <IconButton color="success" onClick={() => restoreOrder(params.row)} size="small">
                <RestoreFromTrash fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar permanentemente">
              <IconButton
                color="error"
                onClick={() => deletePermanently(params.row)}
                size="small"
                sx={{ ml: 0.5 }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    ),
  },
];

  return cols;
}, [
  getStatusChip,
  handleAbastoClick,
  handleEditClick,
  handleViewClick,
  // OJO: quité restoreOrder y deletePermanently del array de deps
  selectionModel,
  isSuperuser,
  companyNameMap,
]);



const deleteOrder = async (row) => {
        if (window.confirm('¿Estás segur@ de eliminar esta orden?')) {
            try {
                const response = await API.patch(`order/${row.id}/`, { is_active: false });
                setOpenSnack(true);
                setSnackMessage("¡Orden eliminada!");
                setSnackSeverity("warning");
                fetchData(); // Actualiza los datos
            } catch (error) {
                console.log(error);
                setOpenSnack(true);
                setSnackMessage("Algo salió mal, intente de nuevo");
                setSnackSeverity("error");
            }
        }
    }
    const deletePermanently = async (order) => {
        if (window.confirm('¿Eliminar permanentemente esta orden? Esta acción no se puede deshacer')) {
            try {
                await API.delete(`order/${order.id}/`);
                setOpenSnack(true);
                setSnackMessage("¡Orden eliminada permanentemente!");
                setSnackSeverity("success");
                fetchData();
            } catch (error) {
                console.error(error);
                setOpenSnack(true);
                setSnackMessage("Error al eliminar");
                setSnackSeverity("error");
            }
        }
    };
    const finalizeOrder = async (row) => {
        if (window.confirm("⚠️ ¿Estás segur@ de finalizar esta orden? 🚚✅")) {
            try {
                const response = await API.patch(`order/${row.id}/`, { orderStatus: 'FI' });
                setOpenSnack(true);
                setSnackMessage("¡Orden finalizada!");
                setSnackSeverity("success");
                fetchData(); // Refresca la tabla
            } catch (error) {
                console.error(error);
                setOpenSnack(true);
                setSnackMessage("Algo salió mal al finalizar la orden");
                setSnackSeverity("error");
            }
        }
    };

    const restoreOrder = async (order) => {
        try {
            await API.patch(`order/${order.id}/`, { is_active: true });
            setOpenSnack(true);
            setSnackMessage("¡Orden restaurada!");
            setSnackSeverity("success");
            fetchData();
        } catch (error) {
            console.error(error);
            setOpenSnack(true);
            setSnackMessage("Error al restaurar");
            setSnackSeverity("error");
        }
    };

    return (
        <>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                width: '100%',
                position: 'relative'
            }}>
                <Box sx={{ flexGrow: 1 }}>
                    <MainText
                        title='Órdenes de compra'
                        subtitle='Listado de las órdenes'
                        icon={ContentPaste}
                        width='auto'
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {filters.status !== 'todas' && (
                        <Chip
                            label={`Estado: ${
                                filters.status === 'PE'
                                    ? 'Pendiente'
                                    : filters.status === 'EP'
                                        ? 'Parcial'
                                        : filters.status === 'EX'
                                            ? 'Vencida'
                                            : 'Finalizado'
                            }`}
                            onDelete={() => handleFilterChange('status', 'todas')}
                        />
                    )}
                    {filters.search && (
                        <Chip
                            label={`Busqueda: "${filters.search}"`}
                            onDelete={() => handleFilterChange('search', '')}
                        />
                    )}
                    {hasStartDate && (
                        <Chip
                            label={`Desde: ${formatDateLabel(filters.startDate)}`}
                            onDelete={() => handleFilterChange('startDate', null)}
                        />
                    )}
                    {hasEndDate && (
                        <Chip
                            label={`Hasta: ${formatDateLabel(filters.endDate)}`}
                            onDelete={() => handleFilterChange('endDate', null)}
                        />
                    )}
                    {showTodayChip && (
                        <Chip
                            label="Mostrando solo hoy"
                            color="info"
                        />
                    )}
                    {!filters.active && (
                        <Chip
                            label="Eliminados"
                            color="error"
                            onDelete={() => handleFilterChange('active', true)}
                        />
                    )}
                </Box>
                <CleanningButton
                    onClick={() => setFilters({
                        search: '',
                        startDate: null,
                        endDate: null,
                        status: 'todas',
                        active: true // Restablece a mostrar solo activos
                    })}
                />


                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        marginRight: '30px'
                    }}>
                        <DatePick
                            label="Fecha inicio"
                            size='small'
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            allowHistoricalDates={true}
                            allowFutureDates={false}
                            maxWidth={200}
                        />
                        <DatePick
                            label="Fecha fin"
                            size='small'
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            allowHistoricalDates={true}
                            allowFutureDates={false}
                            maxWidth={200}
                        />
                        <FormControl sx={{ minWidth: 150, padding: '5px' }}>
                            <InputLabel>Estado</InputLabel>
                            <Select
                                size='small'
                                value={filters.status}
                                onChange={(e) => {
                                    handleFilterChange('status', e.target.value);

                                }}
                                label="Estado"
                            >
                                <MenuItem value='todas'>Todas</MenuItem>
                                <MenuItem value='PE'>Pendiente de Entrega</MenuItem>
                                <MenuItem value='EP'>Entrega Parcial</MenuItem>
                                <MenuItem value='FI'>Finalizado</MenuItem>
                                <MenuItem value='EX'>Vencida</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            variant='outlined'
                            size='small'
                            sx={{ padding: '5px', width: 'auto' }}
                            placeholder='Buscar...'
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <CustomSwitch
                            name="active"
                            label={filters.active ? 'Activo' : 'Eliminado'}
                            onChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
                            initialValue={filters.active}
                        />
                        <Tooltip title="Refrescar">
                            <IconButton size="small" onClick={fetchData} sx={{ color: 'text.secondary' }}>
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>


                    <Box sx={{ marginLeft: 'auto' }}>
                        {selectedOrder && selectedOrder.is_active && (
                            <>
                                <IconOrderPDF
                                    order={{
                                        ...selectedOrder,
                                        order_details: selectedOrder.order_details?.map(detail => ({
                                            ...detail,
                                            product: {
                                                ...detail.product,
                                                description: detail.product?.description || 'Sin descripción'
                                            }
                                        })) || []
                                    }}
                                />
                                <Tooltip title='Eliminar orden'>
                                    <IconButton
                                        color='error'
                                        sx={{ border: '1px solid', marginLeft: '5px' }}
                                        onClick={() => deleteOrder(selectedOrder)}
                                    >
                                        <DeleteRounded />
                                    </IconButton>
                                </Tooltip>
                                {!['FI', 'EX'].includes(String(selectedOrder.orderStatus)) && (
                                    <Tooltip title='Marcar como finalizada'>
                                        <IconButton
                                            color='success'
                                            sx={{ border: '1px solid', marginLeft: '5px' }}
                                            onClick={() => finalizeOrder(selectedOrder)}
                                        >
                                            <Check />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </Box>

                </Box>
            </Box>

            <Box sx={{ height: 450, width: '100%', marginTop: 1 }}>
                <DataGrid
                    rows={orders}
                    columns={columns}
                    loading={isLoading}
                    pageSizeOptions={[5, 10, 20]}
                    pagination
                    onRowSelectionModelChange={handleSelectionChange}
                    rowSelectionModel={selectionModel}
                    disableColumnFilter
                    disableColumnMenu
                    disableVirtualization={false}
                    rowHeight={50}
                    columnHeaderHeight={50}
                    sx={{
                        // ESTILOS PARA EL HEADER PRINCIPAL
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#10173c',
                            color: '#ffffff',
                        },

                        // ESTILOS PARA LAS CELDAS DEL HEADER
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#10173c !important', // Fuerza el color de fondo
                            '&:hover, &:focus, &:active': {
                                backgroundColor: '#10173c !important', // Mantiene el color en interacciones
                            },
                        },

                        // ESTILOS PARA EL TEXTO DEL HEADER
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#ffffff',
                        },

                        // ESTILOS PARA LOS ICONOS (ordenamiento, filtro, etc.)
                        '& .MuiDataGrid-iconButtonContainer': {
                            color: '#ffffff !important',
                        },
                        '& .MuiDataGrid-menuIcon': {
                            color: '#ffffff !important',
                        },

                        // ESTILOS PARA EL BOTÓN DE EXPANSIÓN DEL COLUMNA
                        '& .MuiDataGrid-columnSeparator': {
                            color: '#ffffff !important',
                            '&:hover': {
                                color: '#ffffff !important',
                            },
                        },

                        // ESTILOS PARA LAS CELDAS DEL CUERPO
                        '& .MuiDataGrid-cell': {
                            padding: '0 8px',
                            borderBottom: '1px solid #f0f0f0', // Borde sutil entre filas
                        },

                        // ESTILOS PARA LAS FILAS
                        '& .MuiDataGrid-row': {
                            '&:hover': {
                                cursor: 'pointer',
                                backgroundColor: 'rgba(16, 23, 60, 0.08)', // Hover sutil
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(16, 23, 60, 0.12)',
                                '&:hover': {
                                    backgroundColor: 'rgba(16, 23, 60, 0.15)',
                                },
                            },
                        },

                    }}
                />
                <CustomModal
                    open={open}
                    setOpen={setOpen}
                    handleClose={() => setOpen(false)}
                    component={EditOrder}
                    width='70%'
                    title='Editar detalle de la orden'
                    icon={EditNote}
                    componentProps={{
                        setOpen: setOpen,
                        order: selectedOrder,
                        updateOrder: fetchData,
                        setOrder: setSelectedOrder,
                        onEditAbasto: () => {
                            if (!selectedOrder) return;
                            setOpen(false);
                            handleAbastoClick(selectedOrder);
                        }
                    }}
                />
                <CustomModal
                    open={openView}
                    setOpen={setOpenView}
                    handleClose={() => setOpenView(false)}
                    component={ViewInvoiceDetail}
                    width='70%'
                    title='Detalle de facturación'
                    icon={Visibility}
                    componentProps={{
                        order: selectedOrder,
                        setOpen: setOpenView
                    }}
                />
                <OrderAbastoDialog
                    open={openAbasto}
                    order={abastoOrder}
                    onClose={() => setOpenAbasto(false)}
                    onSaved={fetchData}
                    onNotify={handleAbastoNotify}
                />
                {openSnack && (
                    <CustomSnackBar open={openSnack} setOpen={setOpenSnack} message={snackMessage} severity={snackSeverity} />
                )}
            </Box>
        </>
    );
}
